import { CloudSun, Users } from 'lucide-react'

import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from '@/shared/components/molecules/Dialog/dialog'

interface SmartSlotTypeSelectionModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	onSelectWeather: () => void
	onSelectOccupancy: () => void
}

export const SmartSlotTypeSelectionModal = ({
	open,
	onOpenChange,
	onSelectWeather,
	onSelectOccupancy,
}: SmartSlotTypeSelectionModalProps) => {
	const { t } = useTranslation()
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-2xl p-0 gap-0">
				<DialogHeader className="px-6 pt-6 pb-4">
					<DialogTitle className="text-xl">
						{t('entities.schedule.smart_slot.select_type_title')}
					</DialogTitle>
					<DialogDescription className="sr-only">
						{t('entities.schedule.smart_slot.select_type_description')}
					</DialogDescription>
				</DialogHeader>

				<Separator />

				<div className="px-6 py-6 space-y-3">
					{/* Weather Slot Option */}
					<button
						onClick={onSelectWeather}
						className="w-full p-4 rounded-lg border border-muted hover:bg-accent/50 transition-colors text-left"
					>
						<div className="flex items-start gap-4">
							<div className="flex-shrink-0 w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
								<CloudSun className="w-6 h-6" />
							</div>
							<div className="flex-1">
								<h3 className="font-semibold text-base">
									{t('entities.schedule.smart_slot.weather_slot')}
								</h3>
								<p className="text-sm text-muted-foreground mt-1">
									{t('entities.schedule.smart_slot.weather_slot_description')}
								</p>
							</div>
						</div>
					</button>

					{/* Occupancy Slot Option */}
					<button
						onClick={onSelectOccupancy}
						className="w-full p-4 rounded-lg border border-muted hover:bg-accent/50 transition-colors text-left"
					>
						<div className="flex items-start gap-4">
							<div className="flex-shrink-0 w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
								<Users className="w-6 h-6" />
							</div>
							<div className="flex-1">
								<h3 className="font-semibold text-base">
									{t('entities.schedule.smart_slot.occupancy_slot')}
								</h3>
								<p className="text-sm text-muted-foreground mt-1">
									{t('entities.schedule.smart_slot.occupancy_slot_description')}
								</p>
							</div>
						</div>
					</button>
				</div>

				{/* Info Banner */}
				<div className="px-6 py-3 bg-muted/30 border-t border-b">
					<p className="text-xs text-muted-foreground flex items-start gap-2">
						<span className="flex-shrink-0 mt-0.5">ⓘ</span>
						<span>{t('entities.schedule.smart_slot.integration_info')}</span>
					</p>
				</div>

				<Separator />

				<DialogFooter className="px-6 py-4">
					<Button
						type="button"
						variant="outline"
						onClick={() => onOpenChange(false)}
						className="h-8 rounded-full px-6"
					>
						{t('actions.cancel')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
